import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, FileCheck, Users, Clock, MapPin, Award, Star, Phone, ArrowRight, CheckCircle, ShoppingBag, ChevronLeft, ChevronRight, Sparkles, Moon, Stethoscope, Baby, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const merchSlides = [
  {
    id: 1,
    name: "I Know CPR",
    price: "$20",
    category: "Kids & Adults",
    image: "https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/cu7r1n8c_ChatGPT%20Image%20Mar%206%2C%202026%2C%2011_26_09%20AM.png",
    bgColor: "from-red-500 to-pink-500"
  },
  {
    id: 2,
    name: "Kids Collection",
    price: "$25",
    category: "Fun Designs for Young Lifesavers",
    image: "https://customer-assets.emergentagent.com/job_lifebreath/artifacts/wszpnt2r_ChatGPT%20Image%20Mar%204%2C%202026%2C%2005_46_53%20PM.png",
    bgColor: "from-purple-500 to-indigo-500"
  },
  {
    id: 3,
    name: "Adults Collection",
    price: "$30",
    category: "Professional Designs",
    image: "https://customer-assets.emergentagent.com/job_lifebreath/artifacts/yvxn3hq4_ChatGPT%20Image%20Mar%205%2C%202026%2C%2012_36_43%20PM.png",
    bgColor: "from-indigo-500 to-blue-500"
  }
];

const services = [
  {
    icon: Heart,
    title: 'CPR/First Aid Training',
    description: 'One-on-one and group training for Heartsaver CPR/AED, First Aid, and BLS certification.',
    link: '/cpr-training',
    color: 'bg-gradient-to-br from-[#c31c31] to-[#e05b6d]',
  },
  {
    icon: FileCheck,
    title: 'Blended Courses',
    description: 'Complete online portion at home, then schedule in-person skills check for certification.',
    link: '/blended-courses',
    color: 'bg-gradient-to-br from-[#5a2b8a] to-[#8c53b0]',
  },
  {
    icon: Users,
    title: 'Mobile Notary (GA)',
    description: 'Professional mobile notary services throughout Georgia. We come to you.',
    link: '/mobile-notary',
    color: 'bg-gradient-to-br from-[#4f62c8] to-[#2a2873]',
  },
];

const features = [
  { icon: MapPin, text: 'Mobile Service - We Come to You', color: 'from-[#c31c31] to-[#e05b6d]' },
  { icon: Clock, text: 'Flexible Scheduling', color: 'from-[#5a2b8a] to-[#8c53b0]' },
  { icon: Award, text: 'Professional Certification', color: 'from-[#4f62c8] to-[#2a2873]' },
  { icon: CheckCircle, text: 'Clear, Upfront Pricing', color: 'from-[#c31c31] to-[#5a2b8a]' },
];

const whoWeServe = [
  'Individuals & Families',
  'Churches & Faith Groups',
  'Schools & Daycares',
  'Small Businesses',
  'Healthcare Workers',
  'Caregivers',
];

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-advance slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % merchSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % merchSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + merchSlides.length) % merchSlides.length);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-50 via-white to-purple-50 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%235a2b8a%22%20fill-opacity%3D%220.03%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] text-white text-sm font-medium shadow-lg shadow-[#c31c31]/25">
                <Heart className="w-4 h-4" />
                <span>Certified Life-Saving Training</span>
              </div>
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#2a2873] leading-tight"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="hero-title"
              >
                <span className="bg-gradient-to-r from-[#c31c31] to-[#c31c31] bg-clip-text text-transparent">Life</span><span className="bg-gradient-to-r from-[#5a2b8a] to-[#5a2b8a] bg-clip-text text-transparent">Breath</span> CPR & First Aid
              </h1>
              <p className="text-xl md:text-2xl text-[#5a2b8a] font-semibold leading-relaxed max-w-lg">
                Because Every Breath Matters.
              </p>
              <p className="text-base text-muted-foreground leading-relaxed max-w-lg">
                Empowering individuals, families, and organizations with life-saving skills through accessible, mobile training.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/book-now">
                  <Button 
                    size="lg" 
                    className="rounded-full bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] hover:from-[#a51828] hover:to-[#4a2370] px-8 py-6 text-lg shadow-xl shadow-[#c31c31]/30 hover:shadow-[#c31c31]/40 transition-all hover:scale-105"
                    data-testid="hero-book-cpr-btn"
                  >
                    Book CPR Class
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/mobile-notary">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="rounded-full border-2 border-[#4f62c8] text-[#4f62c8] hover:bg-[#4f62c8]/10 px-8 py-6 text-lg hover:scale-105 transition-all"
                    data-testid="hero-notary-btn"
                  >
                    Mobile Notary
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1755550247429-1e4bce9dd031?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxDUFIlMjByZXN1c2NpdGF0aW9uJTIwdHJhaW5pbmclMjBkdW1teSUyMGhhbmRzfGVufDB8fHx8MTc2OTkxNDY1Mnww&ixlib=rb-4.1.0&q=85"
                  alt="CPR Training - Person practicing on training dummy"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#264653]/40 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 flex items-center gap-3 border-l-4 border-[#c31c31]">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#c31c31] to-[#5a2b8a] flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Call or Text</p>
                  <a href="tel:912-388-6305" className="font-bold text-[#264653]">912-388-6305</a>
                  <p className="text-xs text-muted-foreground">wecare@lifebreathcpr.shop</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURED EVENT BANNER - Back to School Bash */}
      <section className="py-8 bg-gradient-to-r from-blue-600 to-indigo-600" data-testid="featured-event-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6 text-white">
                <div className="hidden sm:block w-24 h-24 rounded-2xl overflow-hidden shadow-lg flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_1d50465d-a7fd-4247-9524-de4b017856ee/artifacts/maizv55h_IMG_0895.jpeg"
                    alt="Back to School Bash"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <Badge className="bg-yellow-400 text-yellow-900 mb-2">🎒 UPCOMING EVENT</Badge>
                  <h3 className="text-2xl md:text-3xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Back to School Bash - CPR & First Aid
                  </h3>
                  <div className="flex flex-wrap gap-4 mt-2 text-white/90">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> August 1st, 2026 • 10 AM - 2 PM
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> Jacksonville, FL
                    </span>
                  </div>
                  <div className="flex gap-4 mt-2">
                    <span className="text-yellow-300 font-bold">Adults: $50</span>
                    <span className="text-green-300 font-bold">Youth: FREE!</span>
                  </div>
                </div>
              </div>
              <Link to="/events">
                <Button 
                  size="lg" 
                  className="bg-white text-blue-600 hover:bg-yellow-400 hover:text-blue-900 rounded-full px-8 py-6 text-lg font-bold shadow-xl hover:scale-105 transition-all"
                  data-testid="event-register-btn"
                >
                  Register Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#2a2873] mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Our <span className="bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-[#5a2b8a]/70 max-w-2xl mx-auto">
              Professional training and notary services that come to you
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={service.link}>
                  <Card 
                    className="h-full hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-0 shadow-lg overflow-hidden group"
                    data-testid={`service-card-${index}`}
                  >
                    <div className={`h-2 ${service.color}`} />
                    <CardContent className="p-8">
                      <div className={`w-16 h-16 rounded-2xl ${service.color} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                        <service.icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 
                        className="text-xl font-bold text-[#2a2873] mb-3"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      >
                        {service.title}
                      </h3>
                      <p className="text-[#5a2b8a]/70 leading-relaxed">
                        {service.description}
                      </p>
                      <div className="mt-4 flex items-center text-[#c31c31] font-semibold group-hover:text-[#5a2b8a]">
                        Learn More <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Pricing Preview */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-red-50 via-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#2a2873] mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Popular <span className="bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-[#5a2b8a]/70">Clear, upfront pricing with no hidden fees</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="text-center p-6 bg-white border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1" data-testid="pricing-preview-1">
                <CardContent className="pt-4">
                  <p className="text-sm text-[#5a2b8a]/70 mb-2 font-medium">Heartsaver CPR/AED</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] bg-clip-text text-transparent">$77</p>
                  <p className="text-sm text-[#5a2b8a]/70 mt-1">One-on-One</p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="text-center p-6 bg-gradient-to-br from-[#c31c31] to-[#5a2b8a] text-white border-0 shadow-xl scale-105 hover:scale-110 transition-all relative" data-testid="pricing-preview-2">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-[#4f62c8] to-[#2a2873] text-white text-xs font-bold rounded-full">
                  POPULAR
                </div>
                <CardContent className="pt-4">
                  <p className="text-sm text-white/80 mb-2 font-medium">CPR/AED & First Aid</p>
                  <p className="text-4xl font-bold">$100</p>
                  <p className="text-sm text-white/80 mt-1">Group Rate (Min 6)</p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="text-center p-6 bg-white border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1" data-testid="pricing-preview-3">
                <CardContent className="pt-4">
                  <p className="text-sm text-[#5a2b8a]/70 mb-2 font-medium">Mobile Notary</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-[#4f62c8] to-[#2a2873] bg-clip-text text-transparent">$25+</p>
                  <p className="text-sm text-[#5a2b8a]/70 mt-1">Travel Fee Starts</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
          <div className="text-center mt-10">
            <Link to="/cpr-training">
              <Button variant="outline" className="rounded-full border-2 border-[#5a2b8a] text-[#5a2b8a] hover:bg-[#5a2b8a]/10" data-testid="view-all-pricing-btn">
                View All Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#2a2873] mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Who We <span className="bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] bg-clip-text text-transparent">Serve</span>
              </h2>
              <p className="text-[#5a2b8a]/70 mb-8 leading-relaxed">
                We bring life-saving training to everyone in our community. Whether you're preparing for a new job, 
                keeping your family safe, or training your team, we're here to help.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {whoWeServe.map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#c31c31] to-[#5a2b8a] flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#2a2873] font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              {features.map((feature, index) => (
                <Card key={index} className={`p-6 border-0 shadow-lg bg-gradient-to-br ${feature.color} text-white hover:scale-105 transition-transform`} data-testid={`feature-card-${index}`}>
                  <CardContent className="p-0">
                    <feature.icon className="w-8 h-8 mb-3" />
                    <p className="font-semibold">{feature.text}</p>
                  </CardContent>
                </Card>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overnight Care Companion Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#2a2873] via-[#5a2b8a] to-[#4f62c8] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="bg-white/20 text-white text-lg px-4 py-2 mb-6">
                <Moon className="w-5 h-5 mr-2 inline" />
                NEW SERVICE
              </Badge>
              <h2 
                className="text-3xl md:text-5xl font-black text-white mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Overnight Care <span className="bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent">Companion</span>
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Compassionate and experienced caregiver with <strong>20+ years</strong> of experience providing safe, dependable overnight care.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                  <Users className="w-6 h-6 text-yellow-400" />
                  <span className="text-white font-medium">Elderly Care</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                  <Heart className="w-6 h-6 text-pink-400" />
                  <span className="text-white font-medium">Disabled Adults</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                  <Baby className="w-6 h-6 text-blue-400" />
                  <span className="text-white font-medium">Special Needs</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                  <Stethoscope className="w-6 h-6 text-green-400" />
                  <span className="text-white font-medium">Post-Surgery</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/overnight-care">
                  <Button size="lg" className="rounded-full bg-white text-[#2a2873] hover:bg-white/90 px-8 py-6 text-lg font-bold shadow-xl">
                    Learn More <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:912-388-6305">
                  <Button size="lg" variant="outline" className="rounded-full border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                    <Phone className="w-5 h-5 mr-2" /> 912-388-6305
                  </Button>
                </a>
              </div>
            </motion.div>
            
            {/* Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-white/10 backdrop-blur-xl border-white/20 overflow-hidden">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <Shield className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white">Why Families Trust Our Care</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {[
                      "20+ Years of Hands-On Experience",
                      "CNA, EMT & Medical Assistant Certified",
                      "CPR / First Aid / BLS Instructor",
                      "Patient, Compassionate & Dependable",
                      "Safety-Focused Care"
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <span className="text-white">{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-8 pt-6 border-t border-white/20 text-center">
                    <p className="text-white/70 mb-2">Serving</p>
                    <div className="flex items-center justify-center gap-2 text-white font-bold">
                      <MapPin className="w-5 h-5 text-yellow-400" />
                      Statesboro, GA & Surrounding Areas
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MERCH BANNER - Highly Visible */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="merch-section">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23fff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
          {/* Floating Elements */}
          <motion.div 
            animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 6, repeat: Infinity }}
            className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-[#c31c31]/30 to-pink-500/30 rounded-full blur-xl"
          />
          <motion.div 
            animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 5, repeat: Infinity, delay: 1 }}
            className="absolute bottom-20 right-10 w-32 h-32 bg-gradient-to-br from-purple-500/30 to-blue-500/30 rounded-full blur-xl"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header with Sparkle Animation */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <motion.div 
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#c31c31] via-pink-500 to-[#5a2b8a] text-white font-bold text-lg mb-6 shadow-2xl shadow-pink-500/30"
            >
              <Sparkles className="w-5 h-5 animate-pulse" />
              <span>NEW MERCH AVAILABLE!</span>
              <Sparkles className="w-5 h-5 animate-pulse" />
            </motion.div>
            <h2 
              className="text-4xl md:text-6xl font-black text-white mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              <span className="bg-gradient-to-r from-white via-pink-200 to-white bg-clip-text text-transparent">LifeBreath</span>{' '}
              <span className="bg-gradient-to-r from-[#c31c31] via-pink-400 to-[#5a2b8a] bg-clip-text text-transparent">Merch</span>
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Show your CPR pride! Exclusive t-shirts for lifesavers of all ages.
            </p>
          </motion.div>

          {/* Slideshow Carousel */}
          <div className="relative max-w-4xl mx-auto">
            {/* Main Slideshow Container */}
            <div className="relative h-[500px] md:h-[550px] rounded-3xl overflow-hidden shadow-2xl shadow-purple-500/30">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  className={`absolute inset-0 bg-gradient-to-br ${merchSlides[currentSlide].bgColor}`}
                >
                  <div className="h-full flex flex-col md:flex-row items-center justify-center p-6 md:p-10">
                    {/* Image Side */}
                    <motion.div 
                      initial={{ scale: 0.8, rotate: -5 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="flex-1 flex items-center justify-center mb-6 md:mb-0"
                    >
                      <img 
                        src={merchSlides[currentSlide].image} 
                        alt={merchSlides[currentSlide].name}
                        className="max-h-[300px] md:max-h-[400px] object-contain drop-shadow-2xl rounded-2xl"
                      />
                    </motion.div>
                    
                    {/* Info Side */}
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                      className="flex-1 text-center md:text-left"
                    >
                      <span className="inline-block px-4 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-4">
                        {merchSlides[currentSlide].category}
                      </span>
                      <h3 className="text-3xl md:text-5xl font-black text-white mb-4">
                        {merchSlides[currentSlide].name}
                      </h3>
                      <div className="text-5xl md:text-7xl font-black text-white mb-6">
                        {merchSlides[currentSlide].price}
                      </div>
                      <Link to="/shop">
                        <Button 
                          size="lg" 
                          className="rounded-full bg-white text-slate-900 hover:bg-white/90 px-8 py-6 text-lg font-bold shadow-xl hover:scale-105 transition-transform"
                        >
                          <ShoppingBag className="w-5 h-5 mr-2" />
                          Shop Now
                        </Button>
                      </Link>
                    </motion.div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Arrows */}
              <button 
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/40 flex items-center justify-center transition-all z-10"
                data-testid="prev-slide-btn"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button 
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/40 flex items-center justify-center transition-all z-10"
                data-testid="next-slide-btn"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Slide Indicators */}
            <div className="flex justify-center gap-3 mt-6">
              {merchSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'w-10 bg-gradient-to-r from-[#c31c31] to-[#5a2b8a]' 
                      : 'w-3 bg-white/40 hover:bg-white/60'
                  }`}
                  data-testid={`slide-indicator-${index}`}
                />
              ))}
            </div>

            {/* Price Tags Below */}
            <div className="flex justify-center gap-4 md:gap-8 mt-8 flex-wrap">
              {merchSlides.map((slide, index) => (
                <motion.button
                  key={slide.id}
                  onClick={() => setCurrentSlide(index)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-6 py-3 rounded-2xl transition-all ${
                    index === currentSlide 
                      ? 'bg-white text-slate-900 shadow-xl' 
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <div className="font-bold">{slide.name}</div>
                  <div className={`text-lg font-black ${index === currentSlide ? 'text-[#c31c31]' : 'text-pink-300'}`}>
                    {slide.price}
                  </div>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-white/60 mb-4">Limited edition designs • Youth & Adult sizes available</p>
            <Link to="/shop">
              <Button 
                size="lg" 
                className="rounded-full bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] hover:from-[#a51828] hover:to-[#4a2370] px-10 py-7 text-xl shadow-2xl shadow-[#c31c31]/30 hover:scale-105 transition-all"
                data-testid="shop-all-merch-btn"
              >
                <ShoppingBag className="w-6 h-6 mr-2" />
                View All Merch
                <ArrowRight className="w-6 h-6 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#c31c31] via-[#5a2b8a] to-[#4f62c8] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2240%22%20height%3D%2240%22%20viewBox%3D%220%200%2040%2040%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22%23fff%22%20fill-opacity%3D%220.1%22%3E%3Cpath%20d%3D%22M0%2040L40%200H20L0%2020M40%2040V20L20%2040%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E')]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Ready to Learn Life-Saving Skills?
            </h2>
            <p className="text-white/90 mb-8 max-w-2xl mx-auto text-lg">
              Call or text us today to schedule your training. We come to you!
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="tel:912-388-6305">
                <Button 
                  size="lg" 
                  className="rounded-full bg-white text-[#c31c31] hover:bg-gray-100 px-8 py-6 text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
                  data-testid="cta-call-btn"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  912-388-6305
                </Button>
              </a>
              <Link to="/book-now">
                <Button 
                  size="lg" 
                  className="rounded-full bg-[#2a2873] text-white hover:bg-[#1a1853] px-8 py-6 text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all border-0"
                  data-testid="cta-book-btn"
                >
                  Book Online
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
